import { msg } from "@lingui/core/macro";
import { createSignal, For, Show } from "solid-js";
import { Apple, Bot, Globe, Mail, Plug } from "lucide-solid";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Label, TextField } from "@/components/ui/text-field";
import { showToast } from "@/components/ui/toast";
import { api, createApiMutation, createApiQuery } from "@/lib/api";
import { adminQueryKeys, type components } from "@mealplan/shared";
import { useI18n } from "@/providers/i18n-provider";
import { useAdminCapabilities } from "../use-admin-capabilities";

type ErrorModel = components["schemas"]["ErrorModel"];
type IntegrationsStatus = components["schemas"]["IntegrationsStatus"];

export function IntegrationsPanel() {
  const { t } = useI18n();
  const { can } = useAdminCapabilities();
  const [testOpen, setTestOpen] = createSignal(false);
  const [testEmail, setTestEmail] = createSignal("");

  const query = createApiQuery<IntegrationsStatus, ErrorModel>(
    () => adminQueryKeys.integrations(),
    async () => api.GET("/api/admin/integrations", {}),
    () => ({ staleTime: 30_000 }),
  );

  const testEmailMutation = createApiMutation(async (vars: { to?: string }) =>
    api.POST("/api/admin/integrations/test-email", { body: vars }),
  );

  const rows = () => {
    const { data } = query;
    return [
      { key: "google", label: t(msg`Google sign-in`), icon: Globe, on: data?.google ?? false },
      { key: "apple", label: t(msg`Apple sign-in`), icon: Apple, on: data?.apple ?? false },
      { key: "smtp", label: t(msg`Email (SMTP)`), icon: Mail, on: data?.smtpConfigured ?? false },
      { key: "ai", label: t(msg`AI features`), icon: Bot, on: data?.aiAvailable ?? false },
    ];
  };

  const handleTest = () => {
    testEmailMutation.mutate(
      { to: testEmail().trim() || undefined },
      {
        onSuccess: (res) => {
          const { sentTo } = res;
          showToast(t(msg`Test email sent to ${sentTo}`), "success");
          setTestOpen(false);
          setTestEmail("");
        },
        onError: () => {
          showToast(t(msg`Failed to send test email`), "error");
        },
      },
    );
  };

  return (
    <div class="space-y-6">
      <div class="flex items-center gap-3">
        <Plug class="h-8 w-8 text-primary" />
        <div>
          <h2 class="text-3xl font-bold tracking-tight">{t(msg`Integrations`)}</h2>
          <p class="text-sm text-muted-foreground">
            {t(msg`Health of external providers (no secrets shown).`)}
          </p>
        </div>
      </div>

      <Show
        when={!query.isLoading}
        fallback={
          <div class="space-y-3">
            <For each={Array.from({ length: 4 }, () => 0)}>
              {() => <Skeleton class="h-16 w-full rounded-xl" />}
            </For>
          </div>
        }
      >
        <div class="space-y-3">
          <For each={rows()}>
            {(row) => (
              <Card>
                <CardContent class="p-4 flex items-center justify-between">
                  <div class="flex items-center gap-3">
                    <row.icon class="h-5 w-5 text-muted-foreground" />
                    <span class="font-medium">{row.label}</span>
                  </div>
                  <div class="flex items-center gap-3">
                    <Badge variant={row.on ? "default" : "secondary"}>
                      {row.on ? t(msg`ON`) : t(msg`OFF`)}
                    </Badge>
                    <Show when={row.key === "smtp" && row.on && can("integrations.testEmail")}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setTestOpen(true);
                        }}
                      >
                        {t(msg`Send test email`)}
                      </Button>
                    </Show>
                  </div>
                </CardContent>
              </Card>
            )}
          </For>
        </div>
      </Show>

      <Dialog open={testOpen()} onOpenChange={setTestOpen} contentClass="max-w-[420px] p-6">
        <DialogTitle class="text-xl font-bold mb-2">{t(msg`Send test email`)}</DialogTitle>
        <DialogDescription class="text-sm text-muted-foreground mb-4">
          {t(msg`Leave blank to send to the configured SMTP_FROM address.`)}
        </DialogDescription>
        <div class="space-y-2">
          <Label for="test-email">{t(msg`Recipient`)}</Label>
          <TextField
            id="test-email"
            type="email"
            placeholder={t(msg`your@email.com`)}
            value={testEmail()}
            onInput={(e) => {
              setTestEmail(e.currentTarget.value);
            }}
          />
        </div>
        <div class="flex justify-end gap-3 mt-6">
          <Button
            variant="ghost"
            onClick={() => {
              setTestOpen(false);
            }}
          >
            {t(msg`Cancel`)}
          </Button>
          <Button disabled={testEmailMutation.isPending} onClick={handleTest}>
            {t(msg`Send`)}
          </Button>
        </div>
      </Dialog>
    </div>
  );
}
